import { useState } from 'react';
import { Users, Plus, Minus, ArrowLeft } from 'lucide-react';

export function OnboardingServings({ onComplete, onBack, error = '', submitting = false }) {
  const [servings, setServings] = useState({
    breakfast: 2,
    lunch: 2,
    dinner: 2,
  });

  const updateServing = (mealType, delta) => {
    setServings(prev => ({
      ...prev,
      [mealType]: Math.max(1, Math.min(10, prev[mealType] + delta))
    }));
  };

  const handleComplete = () => {
    onComplete({ defaultServings: servings });
  };

  return (
    <div className="min-h-screen bg-[#F8F7F5] flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        {/* Brand Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-[#6B7A54] mb-2 text-center">
            Welcome to MealPlan Genie
          </h1>
          <div className="flex items-center justify-center gap-2 text-[#757575]">
            <div className="w-2 h-2 rounded-full bg-primary"></div>
            <div className="w-2 h-2 rounded-full bg-primary"></div>
            <div className="w-2 h-2 rounded-full bg-primary"></div>
          </div>
        </div>

        {/* Content Card */}
        <div className="bg-white rounded-3xl shadow-sm p-8">
          {/* Back Button */}
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-[#757575] hover:text-gray-900 transition-colors mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back</span>
          </button>

          {/* Icon */}
          <div className="w-16 h-16 bg-gradient-to-br from-primary/20 to-accent/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Users className="w-8 h-8 text-primary" />
          </div>

          {/* Title and Description */}
          <h2 className="text-2xl font-semibold text-center mb-3">
            Default Serving Sizes
          </h2>
          <p className="text-[#757575] text-center mb-8">
            Set the default number of servings for each meal type. You can adjust this anytime in your preferences.
          </p>

          {/* Serving Controls */}
          <div className="space-y-4 mb-8">
            {/* Breakfast */}
            <div className="bg-[#F8F7F5] rounded-2xl p-4">
              <div className="flex items-center justify-between">
                <span className="font-medium text-gray-900">Breakfast</span>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => updateServing('breakfast', -1)}
                    disabled={servings.breakfast <= 1}
                    className="w-8 h-8 rounded-full bg-white hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center transition-colors shadow-sm"
                    aria-label="Decrease breakfast servings"
                  >
                    <Minus className="w-3.5 h-3.5 text-gray-700" />
                  </button>
                  <div className="w-12 text-center">
                    <span className="text-xl font-semibold text-gray-900">
                      {servings.breakfast}
                    </span>
                  </div>
                  <button
                    onClick={() => updateServing('breakfast', 1)}
                    disabled={servings.breakfast >= 10}
                    className="w-8 h-8 rounded-full bg-primary hover:shadow-md disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center transition-all"
                    aria-label="Increase breakfast servings"
                  >
                    <Plus className="w-3.5 h-3.5 text-white" />
                  </button>
                </div>
              </div>
            </div>

            {/* Lunch */}
            <div className="bg-[#F8F7F5] rounded-2xl p-4">
              <div className="flex items-center justify-between">
                <span className="font-medium text-gray-900">Lunch</span>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => updateServing('lunch', -1)}
                    disabled={servings.lunch <= 1}
                    className="w-8 h-8 rounded-full bg-white hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center transition-colors shadow-sm"
                    aria-label="Decrease lunch servings"
                  >
                    <Minus className="w-3.5 h-3.5 text-gray-700" />
                  </button>
                  <div className="w-12 text-center">
                    <span className="text-xl font-semibold text-gray-900">
                      {servings.lunch}
                    </span>
                  </div>
                  <button
                    onClick={() => updateServing('lunch', 1)}
                    disabled={servings.lunch >= 10}
                    className="w-8 h-8 rounded-full bg-primary hover:shadow-md disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center transition-all"
                    aria-label="Increase lunch servings"
                  >
                    <Plus className="w-3.5 h-3.5 text-white" />
                  </button>
                </div>
              </div>
            </div>

            {/* Dinner */}
            <div className="bg-[#F8F7F5] rounded-2xl p-4">
              <div className="flex items-center justify-between">
                <span className="font-medium text-gray-900">Dinner</span>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => updateServing('dinner', -1)}
                    disabled={servings.dinner <= 1}
                    className="w-8 h-8 rounded-full bg-white hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center transition-colors shadow-sm"
                    aria-label="Decrease dinner servings"
                  >
                    <Minus className="w-3.5 h-3.5 text-gray-700" />
                  </button>
                  <div className="w-12 text-center">
                    <span className="text-xl font-semibold text-gray-900">
                      {servings.dinner}
                    </span>
                  </div>
                  <button
                    onClick={() => updateServing('dinner', 1)}
                    disabled={servings.dinner >= 10}
                    className="w-8 h-8 rounded-full bg-primary hover:shadow-md disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center transition-all"
                    aria-label="Increase dinner servings"
                  >
                    <Plus className="w-3.5 h-3.5 text-white" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Inline error (no toasts). role="alert" so it is announced when it appears. */}
          {error && (
            <p role="alert" className="mb-4 rounded-xl border border-[#D4183D] bg-[#FEF3F2] px-4 py-3 text-sm text-[#B42318]">
              {error}
            </p>
          )}

          {/* Action Buttons */}
          <div className="space-y-3">
            <button
              onClick={handleComplete}
              disabled={submitting}
              aria-busy={submitting || undefined}
              className="w-full py-3 bg-primary text-[#2D3319] rounded-full hover:shadow-lg transition-all font-medium disabled:cursor-wait disabled:opacity-70"
            >
              {submitting ? 'Getting started…' : 'Get Started'}
            </button>
          </div>
        </div>

        {/* Progress Text */}
        <p className="text-center text-sm text-[#757575] mt-4">
          Step 3 of 3
        </p>
      </div>
    </div>
  );
}